import { CAZA, SENDA, VERT } from '@somnhold/content';
import { cellId } from '../grid.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import type { ArchesRules, DenRules, LeapsRules, VertRules } from '../vertebras-types.ts';
import { ARCHES_RADIUS, DEN_RADIUS, LEAPS_RADIUS, type ArchesLayout, type DenLayout, type LeapsLayout } from './vertebras-layout.ts';
import { archesGoal, archesHold, archesOrder, archesResult, archesTick } from './arches.ts';
import { leapsGoal, leapsOrder, leapsResult, leapsTick } from './leaps.ts';
import { denGoal, denHold, denOrder, denResult, denTick } from './den.ts';

/**
 * The Long Hunt's chapter 2 (docs/sendas/barbaro.md, «Las Vértebras»): its rules from their layouts (the fight's builder
 * and the renderer's scenes build them the same way; lives in hit points worked out by the caller, seconds of D) and the
 * one door battle/senda.ts goes through for its three acts (`rules.senda.vert`).
 */

export const archesRules = (L: ArchesLayout, wolfHp: number): ArchesRules => {
  const A = VERT.arches;
  return {
    path: L.path.map((p) => ({ ...p })),
    heads: L.heads.map((p) => ({ ...p })),
    posts: L.posts.map((p) => ({ ...p })),
    mark: L.mark,
    balk: L.balk,
    over: L.over,
    end: { ...L.end },
    plan: L.plan.map((g) => ({ ...g })),
    warn: A.gust.warn,
    push: A.gust.push,
    fallBp: A.gust.fallBp,
    stun: A.gust.stun,
    post: A.post,
    wolfHp,
    pack: { first: A.pack.first, every: [A.pack.every[0], A.pack.every[1]], band: [A.pack.band[0], A.pack.band[1]], behind: A.pack.behind, behindEvery: A.pack.behindEvery },
    crows: { ...A.crows },
    nudge: A.nudge,
    nudgeReach: A.nudgeReach,
    endReach: A.end,
    pace: SENDA.npc.speed + 10,
  };
};

export const leapsRules = (L: LeapsLayout): LeapsRules => {
  const R = LEAPS_RADIUS + 2;
  const pairs: [number, number][] = [];
  L.floor.forEach((f, k) => {
    for (const h of f) {
      const c = cellId(R, h.q, h.r);
      if (c >= 0) pairs.push([c, k]);
    }
  });
  pairs.sort((a, b) => a[0] - b[0]);
  return {
    arches: L.arches.map((p) => ({ ...p })),
    edges: L.graph.edges.map((e) => [e[0], e[1]] as [number, number]),
    marks: L.graph.marks.slice(),
    start: L.graph.start,
    cells: pairs.map((p) => p[0]),
    cellArch: pairs.map((p) => p[1]),
    rope: VERT.leaps.rope,
    fallBp: VERT.leaps.fallBp,
    example: L.example,
  };
};

export const denRules = (L: DenLayout, wolfHp: number, howlerHp: number, stoneHit: number): DenRules => {
  const D = VERT.den;
  const H = CAZA.howler;
  const S = CAZA.stone;
  const R = DEN_RADIUS + 2;
  return {
    mouth: { ...L.mouth },
    perches: L.perches.map((p) => ({ ...p })),
    leash: D.leash,
    boat: { ...L.boat },
    bera: { ...L.bera },
    terron: { ...L.terron },
    sten: { ...L.sten },
    slabs: L.slabs.map((sl) => ({ cells: sl.cells.map((h) => cellId(R, h.q, h.r)).sort((a, b) => a - b), at: { ...sl.at } })),
    stones: L.stones.map((p) => ({ ...p })),
    rolls: L.rolls.map((p) => ({ ...p })),
    launch: D.launch,
    flinch: D.flinch,
    first: D.first,
    every: [D.every[0], D.every[1]],
    band: [D.band[0], D.band[1]],
    crows: { ...D.crows },
    wolfHp,
    howler: { hp: howlerHp, biteBp: H.biteBp, reload: H.reload, reach: H.reach, every: H.every, first: H.first, rear: H.rear, howl: H.howl, cutBp: H.cutBp, stun: H.stun, raise: H.raise, healBp: H.healBp, bites: H.bites, back: H.back, keep: H.keep },
    firsts: [D.firsts[0], D.firsts[1]],
    doubleBp: D.doubleBp,
    rush: { ...D.rush },
    creak: D.creak,
    fallBp: D.fallBp,
    stone: { stand: S.stand, reach: S.reach, roll: 9000, speed: S.speed, width: S.width, hit: stoneHit, stun: S.stun },
    board: D.board,
  };
};

/** The radius of each act's board. */
export const vertRadius = (kind: VertRules['kind']): number => (kind === 'arches' ? ARCHES_RADIUS : kind === 'leaps' ? LEAPS_RADIUS : DEN_RADIUS);

const vr = (s: BattleState): VertRules | undefined => s.rules?.senda?.vert;

export const vertTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = vr(s);
  if (r?.arches) archesTick(s, cue);
  else if (r?.leaps) leapsTick(s, cue);
  else if (r?.den) denTick(s, cue);
};

/** The hero's tap on one of the act's marks. */
export const vertOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = vr(s);
  if (r?.arches) archesOrder(s, h, at);
  else if (r?.leaps) leapsOrder(s, h, at);
  else if (r?.den) denOrder(s, h, at);
};

/** Another order of the hero's own: the push it was sent to is off. */
export const vertMarkOff = (s: BattleState): void => {
  const d = s.senda?.vert?.den;
  if (d) d.job = -1;
};

export const vertGoal = (s: BattleState): { done: number; need: number } => {
  const r = vr(s);
  return r?.arches ? archesGoal(s) : r?.leaps ? leapsGoal(s) : denGoal(s);
};

export const vertResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = vr(s);
  return r?.arches ? archesResult(s) : r?.leaps ? leapsResult(s) : r?.den ? denResult(s) : null;
};

/** The act moved this nightmare itself (the den's howlers and the wolves going for the boat). */
export const vertHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => {
  const r = vr(s);
  if (r?.den) return denHold(s, e, px);
  if (r?.arches) return archesHold(s, e);
  return false;
};
